import { useState } from 'react'
import { Head, Link } from '@inertiajs/react'
import { ExternalLink } from 'lucide-react'
import type { Organization, TechStackCategory, ArtifactType } from '@/types/organization'
import { HyrinWordmark } from '@/components/Shared/HyrinWordmark'

interface OrganizationShowProps {
  organization: Organization
}

const CATEGORY_ORDER: TechStackCategory[] = ['language', 'framework', 'infra', 'deployment', 'llm', 'ai_stack']

const CATEGORY_LABELS: Record<TechStackCategory, string> = {
  language: 'Languages',
  framework: 'Frameworks',
  infra: 'Infrastructure',
  deployment: 'Deployment',
  llm: 'LLMs',
  ai_stack: 'AI Stack',
}

const REMOTE_POLICY_LABELS: Record<'remote' | 'hybrid' | 'onsite', string> = {
  remote: 'Remote',
  hybrid: 'Hybrid',
  onsite: 'On-site',
}

const ARTIFACT_LABELS: Partial<Record<ArtifactType, string>> = {
  product_demo: 'Product demo',
  blog: 'Blog',
  technical_writeup: 'Technical writeup',
  case_study: 'Case study',
  open_source_repo: 'Open-source',
  design_doc: 'Design doc',
  evaluation_example: 'Eval example',
  workflow: 'Workflow',
  demo_video: 'Demo video',
  founder_memo: 'Founder memo',
}

function BannerLandscape() {
  return (
    <svg
      viewBox="0 0 1300 224"
      preserveAspectRatio="xMidYMid slice"
      className="h-full w-full"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="banner-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#081812" />
          <stop offset="50%" stopColor="#0F3828" />
          <stop offset="100%" stopColor="#15604A" />
        </linearGradient>
        <radialGradient id="banner-moon-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#CDE5DB" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#CDE5DB" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Sky */}
      <rect width="1300" height="224" fill="url(#banner-sky)" />
      {/* Moon glow + disc */}
      <circle cx="1095" cy="52" r="44" fill="url(#banner-moon-glow)" />
      <circle cx="1095" cy="52" r="17" fill="#D4E8DE" opacity="0.45" />
      {/* Stars */}
      <circle cx="200" cy="30" r="1.5" fill="#CDE5DB" opacity="0.6" />
      <circle cx="340" cy="18" r="1.2" fill="#CDE5DB" opacity="0.5" />
      <circle cx="450" cy="42" r="1.5" fill="#CDE5DB" opacity="0.7" />
      <circle cx="620" cy="22" r="1.0" fill="#CDE5DB" opacity="0.55" />
      <circle cx="750" cy="35" r="1.5" fill="#CDE5DB" opacity="0.6" />
      <circle cx="880" cy="15" r="1.2" fill="#CDE5DB" opacity="0.45" />
      <circle cx="150" cy="55" r="1.0" fill="#CDE5DB" opacity="0.5" />
      <circle cx="520" cy="12" r="1.5" fill="#CDE5DB" opacity="0.65" />
      <circle cx="1200" cy="38" r="1.2" fill="#CDE5DB" opacity="0.55" />
      <circle cx="960" cy="28" r="1.0" fill="#CDE5DB" opacity="0.5" />
      <circle cx="70" cy="25" r="1.2" fill="#CDE5DB" opacity="0.4" />
      <circle cx="1240" cy="62" r="1.0" fill="#CDE5DB" opacity="0.4" />
      {/* Far mountain range */}
      <path
        d="M0,140 L80,95 L160,115 L240,78 L320,100 L400,65 L480,88 L560,58 L640,82 L720,62 L800,90 L880,68 L960,95 L1040,72 L1120,88 L1200,70 L1300,85 L1300,224 L0,224 Z"
        fill="#163A2A"
        opacity="0.88"
      />
      {/* Mid range */}
      <path
        d="M0,165 L70,138 L150,152 L230,128 L330,148 L430,120 L530,145 L630,130 L730,155 L830,135 L930,158 L1030,138 L1130,160 L1230,142 L1300,152 L1300,224 L0,224 Z"
        fill="#0E2B1E"
      />
      {/* Near hills */}
      <path
        d="M0,188 L90,172 L200,182 L330,165 L480,178 L620,168 L760,180 L890,170 L1020,184 L1150,172 L1300,180 L1300,224 L0,224 Z"
        fill="#091A12"
      />
    </svg>
  )
}

export default function OrganizationShow({ organization }: OrganizationShowProps) {
  const [showFitNote, setShowFitNote] = useState(true)

  return (
    <>
      <Head title={`${organization.name} — Hyrin`} />

      <div className="min-h-screen bg-muted">
        {/* Header */}
        <header className="flex h-[60px] items-center justify-between bg-muted px-5 sm:px-9">
          <Link href="/" className="flex items-center gap-[9px]">
            <HyrinWordmark />
          </Link>
          <div className="flex items-center gap-[18px]">
            <div className="hidden items-center gap-[18px] sm:flex">
              <Link href="/engineers" className="font-sans text-[14px] text-muted-foreground hover:text-foreground">
                Engineers
              </Link>
              <button type="button" className="cursor-pointer font-sans text-[14px] text-muted-foreground hover:text-foreground">
                Settings
              </button>
            </div>
            <div className="flex size-[30px] items-center justify-center rounded-full bg-primary font-heading text-[12px] font-bold text-primary-foreground">
              {organization.initials}
            </div>
          </div>
        </header>

        {/* Banner */}
        <div className="h-[160px] overflow-hidden sm:h-[280px]">
          {organization.bannerImage ? (
            <img
              src={organization.bannerImage}
              alt={`${organization.name} banner`}
              className="h-full w-full object-cover"
            />
          ) : (
            <BannerLandscape />
          )}
        </div>

        {/* Profile header */}
        <div className="border-b border-border bg-background">
          <div className="mx-auto max-w-[1120px] px-5 sm:px-8">

            {/* Row 1: Badge (overlaps banner) + verification pills */}
            <div className="flex items-end gap-4 pt-3">
              <div className="-mt-[48px] flex size-[96px] shrink-0 items-center justify-center rounded-[24px] bg-primary font-heading text-[30px] font-bold text-primary-foreground shadow-lg ring-4 ring-white sm:-mt-[68px] sm:size-[128px] sm:text-[42px]">
                {organization.initials}
              </div>
              <div className="mb-1 flex gap-2">
                {organization.emailVerified && (
                  <span className="rounded-full bg-secondary px-3 py-[5px] font-mono text-[11px] font-semibold text-primary">
                    ✓ Email
                  </span>
                )}
                {organization.phoneVerified && (
                  <span className="rounded-full bg-secondary px-3 py-[5px] font-mono text-[11px] font-semibold text-primary">
                    ✓ Phone
                  </span>
                )}
              </div>
            </div>

            {/* Row 2: Name/type (left) + citizenship score + CTAs (right) */}
            <div className="mt-4 flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between sm:gap-8">
              <div>
                <div className="mb-[6px] flex items-center gap-3">
                  <h1 className="font-heading text-[28px] font-bold leading-[1.05] tracking-[-0.02em] text-foreground sm:text-[34px]">
                    {organization.name}
                  </h1>
                  <span className="rounded-full border border-border px-3 py-[5px] font-mono text-[11px] uppercase text-muted-foreground">
                    {organization.orgType.replace(/_/g, ' ')}
                  </span>
                </div>
              </div>

              <div className="flex flex-col items-stretch gap-4 sm:shrink-0 sm:flex-row sm:items-start sm:gap-6">
                <div className="border-t border-border pt-3 sm:border-l sm:border-t-0 sm:pl-6 sm:pt-0 sm:text-right">
                  <p className="mb-1 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    Citizenship Score
                  </p>
                  <span className="font-heading text-[40px] font-bold leading-none text-primary sm:text-[48px]">
                    {organization.citizenshipScore}
                  </span>
                  <p className="mt-1 font-mono text-[14px] text-muted-foreground">Top 12% of employers</p>
                </div>
                <div className="flex flex-col items-stretch gap-2">
                  <button
                    className="h-[44px] rounded-[11px] bg-foreground px-5 font-sans text-[15px] font-semibold text-background transition-opacity hover:opacity-90"
                    onClick={() => {
                      document.getElementById('open-roles')?.scrollIntoView({ behavior: 'smooth' })
                    }}
                  >
                    View open roles
                  </button>
                  <a
                    href={`https://${organization.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-[44px] items-center justify-center gap-[6px] rounded-[11px] border border-border px-5 font-sans text-[15px] font-semibold text-foreground transition-colors hover:bg-secondary"
                  >
                    Visit Website
                    <ExternalLink className="size-[14px]" />
                  </a>
                </div>
              </div>
            </div>

            {/* Row 3: tagline */}
            <div className="mt-3 pb-7">
              <p className="max-w-[720px] font-heading text-[20px] leading-[1.55] tracking-[-0.01em] text-foreground">
                {organization.tagline}
              </p>
            </div>
          </div>
        </div>

        {/* Fit note banner */}
        {showFitNote && organization.fitNote !== null && (
          <div className="border-b border-border bg-background">
            <div className="mx-auto max-w-[1120px] px-5 sm:px-8">
              <div className="flex items-center justify-between border-l-2 border-primary py-4 pl-4">
                <div className="flex items-start gap-3">
                  <span className="mt-[2px] shrink-0 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                    // fit note
                  </span>
                  <p className="font-sans text-[16px] leading-[1.5] text-foreground">{organization.fitNote}</p>
                </div>
                <button
                  onClick={() => setShowFitNote(false)}
                  className="ml-6 shrink-0 font-mono text-[16px] text-muted-foreground hover:text-foreground"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <main className="mx-auto max-w-[1120px] px-5 py-8 pb-20 sm:px-8">

          {/* Tech stack + right column */}
          <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {/* Tech stack */}
            <div className="rounded-[13px] border border-border bg-card p-[22px]">
              <h2 className="mb-5 font-heading text-[18px] font-bold text-foreground">Tech stack</h2>
              <div className="flex flex-col gap-4">
                {CATEGORY_ORDER.map((category) => {
                  const group = organization.techStack.find((g) => g.category === category)
                  if (!group) {return null}
                  return (
                    <div key={category}>
                      <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                        {CATEGORY_LABELS[category]}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {group.items.map((item) => (
                          <span
                            key={item}
                            className="rounded-[6px] border border-border px-[10px] py-[5px] font-mono text-[13px] text-foreground"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Right column: hiring process + citizenship */}
            <div className="flex flex-col gap-5">
              {/* Hiring process */}
              <div className="rounded-[13px] border border-border bg-card p-[22px]">
                <div className="mb-4 flex items-center gap-3">
                  <h2 className="font-heading text-[18px] font-bold text-foreground">How we hire</h2>
                  {organization.isCandidateFriendly && (
                    <span className="rounded-full bg-secondary px-3 py-[4px] font-mono text-[11px] font-semibold text-primary">
                      Candidate-friendly ✓
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-4">
                  <div>
                    <p className="mb-1 font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                      Evaluation
                    </p>
                    <p className="font-sans text-[16px] font-semibold leading-[1.5] text-foreground">
                      {organization.hiringProcess.evaluationProcess}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="mb-1 font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                        Response time
                      </p>
                      <p className="font-sans text-[16px] font-semibold text-foreground">
                        {organization.hiringProcess.responseTimeDays} business days
                      </p>
                    </div>
                    <div>
                      <p className="mb-1 font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                        Take-home
                      </p>
                      <p className="font-sans text-[16px] font-semibold text-foreground">
                        {organization.hiringProcess.additionalTakeHomes ? 'Paid take-home' : 'None'}
                      </p>
                    </div>
                    <div>
                      <p className="mb-1 font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                        Remote policy
                      </p>
                      <p className="font-sans text-[16px] font-semibold text-foreground">
                        {REMOTE_POLICY_LABELS[organization.hiringProcess.remotePolicy]}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Citizenship criteria */}
              <div className="rounded-[13px] border border-border bg-card p-[22px]">
                <h2 className="mb-4 font-heading text-[18px] font-bold text-foreground">
                  Citizenship criteria
                </h2>
                <div className="flex flex-col gap-[10px]">
                  {organization.citizenshipCriteria.map((criterion) => (
                    <div key={criterion.label} className="flex items-center justify-between">
                      <div className="flex items-center gap-[7px]">
                        <span
                          className={`font-mono text-[12px] ${criterion.met ? 'text-primary' : 'text-muted-foreground'}`}
                        >
                          {criterion.met ? '✓' : '○'}
                        </span>
                        <span className="font-sans text-[15px] text-foreground">{criterion.label}</span>
                      </div>
                      <span className="font-mono text-[11px] text-muted-foreground">{criterion.points}pts</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Problems */}
          <div className="mb-6">
            <div className="mb-4 flex items-baseline gap-3">
              <h2 className="font-heading text-[21px] font-bold text-foreground">
                Problems we&apos;re tackling
              </h2>
              <span className="font-mono text-[14px] text-muted-foreground">// what we build</span>
            </div>
            <div className="flex flex-col gap-4">
              {organization.problems.map((problem) => (
                <div
                  key={problem.problemStatement}
                  className="rounded-[13px] border border-border bg-card p-[22px]"
                >
                  <p className="mb-[6px] font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                    Problem
                  </p>
                  <p className="mb-4 font-sans text-[16px] font-semibold leading-[1.5] text-foreground">
                    {problem.problemStatement}
                  </p>
                  <p className="mb-[6px] font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                    Why it&apos;s hard
                  </p>
                  <p className="mb-4 font-sans text-[16px] leading-[1.5] text-muted-foreground">
                    {problem.whyHard}
                  </p>
                  <p className="mb-[6px] font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                    You may fit if
                  </p>
                  <p className="font-sans text-[16px] leading-[1.5] text-primary">{problem.youMayFitIf}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Open roles */}
          <div className="mb-6" id="open-roles">
            <div className="mb-4 flex items-center gap-3">
              <h2 className="font-heading text-[21px] font-bold text-foreground">Open roles</h2>
              <span className="rounded-full bg-secondary px-2 py-[3px] font-mono text-[11px] font-bold text-primary">
                {organization.positions.length}
              </span>
            </div>
            <div className="flex flex-col gap-3">
              {organization.positions.map((position) => (
                <div
                  key={position.id}
                  className="flex flex-col gap-3 rounded-[13px] border border-border bg-card px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                >
                  <div>
                    <p className="font-sans text-[16px] font-bold text-foreground">{position.name}</p>
                    <p className="mt-[3px] font-sans text-[14px] text-muted-foreground">{position.location}</p>
                  </div>
                  <div className="flex items-center justify-between gap-4 sm:justify-end">
                    {position.salaryDisplay !== null ? (
                      <span className="font-mono text-[14px] font-semibold text-primary">
                        {position.salaryDisplay}
                      </span>
                    ) : (
                      <span className="font-sans text-[14px] italic text-muted-foreground">
                        Salary shared with matched candidates
                      </span>
                    )}
                    <button className="font-mono text-[13px] font-semibold text-primary hover:underline">
                      See role →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Proof of work */}
          <div>
            <div className="mb-4 flex items-baseline gap-3">
              <h2 className="font-heading text-[21px] font-bold text-foreground">Proof of work</h2>
              <span className="font-mono text-[14px] text-muted-foreground">// what we&apos;ve shipped</span>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {organization.artifacts.map((artifact) => (
                <div key={artifact.url} className="rounded-[13px] border border-border bg-card p-[18px]">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                      {ARTIFACT_LABELS[artifact.type] ?? artifact.type}
                    </p>
                    <ExternalLink className="size-[14px] text-muted-foreground" />
                  </div>
                  <a
                    href={artifact.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-sans text-[16px] font-semibold text-foreground hover:text-primary"
                  >
                    {artifact.title}
                  </a>
                  <p className="mt-1 font-sans text-[14px] leading-[1.45] text-muted-foreground">
                    {artifact.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    </>
  )
}
